import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save } from 'lucide-react';

function mergeContent(existing) {
  const defaults = {
    state_assessment: { participates: null, accommodations: '', alternate_assessment: false, notes: '' },
    district_assessment: { participates: null, accommodations: '', alternate_assessment: false, notes: '' },
    notes: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.state_assessment) {
    return { ...defaults, notes: existing.notes };
  }
  return {
    ...defaults,
    ...existing,
    state_assessment: { ...defaults.state_assessment, ...(existing.state_assessment || {}) },
    district_assessment: { ...defaults.district_assessment, ...(existing.district_assessment || {}) },
  };
}

function AssessmentBlock({ label, data, onChange, testIdPrefix }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`${testIdPrefix}-block`}>
      <h3 className="font-medium text-sm text-[#1C1C1C] mb-4">{label}</h3>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-2 block">Will the student participate?</label>
          <div className="flex gap-4">
            {[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
              { value: null, label: 'Not Determined' },
            ].map(opt => (
              <label key={String(opt.value)} className="flex items-center gap-2 text-sm text-[#595959] cursor-pointer">
                <input
                  type="radio"
                  name={`${testIdPrefix}-participates`}
                  checked={data.participates === opt.value}
                  onChange={() => onChange({ ...data, participates: opt.value })}
                  className="text-[#3B6A9B]"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Testing Accommodations</label>
          <Input
            value={data.accommodations}
            onChange={e => onChange({ ...data, accommodations: e.target.value })}
            placeholder="List accommodations used during testing"
            className="rounded-xl border-[#E5E5E5] text-sm h-9"
            data-testid={`${testIdPrefix}-accommodations`}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.alternate_assessment}
            onChange={e => onChange({ ...data, alternate_assessment: e.target.checked })}
            className="rounded"
            data-testid={`${testIdPrefix}-alternate`}
          />
          <label className="text-sm text-[#595959]">Alternate assessment participation</label>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notes</label>
          <Textarea
            value={data.notes}
            onChange={e => onChange({ ...data, notes: e.target.value })}
            placeholder="Additional notes..."
            rows={2}
            className="rounded-xl border-[#E5E5E5] text-sm"
            data-testid={`${testIdPrefix}-notes`}
          />
        </div>
      </div>
    </div>
  );
}

export default function AssessmentEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  const handleSave = () => onSave(content);

  return (
    <div className="max-w-4xl space-y-6" data-testid="assessment-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="assessment-title">Assessment</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="assessment-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <AssessmentBlock
        label="State Assessment Participation"
        data={content.state_assessment}
        onChange={data => setContent(prev => ({ ...prev, state_assessment: data }))}
        testIdPrefix="state-assessment"
      />

      <AssessmentBlock
        label="District-Wide Assessment Participation"
        data={content.district_assessment}
        onChange={data => setContent(prev => ({ ...prev, district_assessment: data }))}
        testIdPrefix="district-assessment"
      />

      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="assessment-general-notes">
        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">General Assessment Notes</label>
        <Textarea
          value={content.notes}
          onChange={e => setContent(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Additional assessment notes..."
          rows={3}
          className="rounded-xl border-[#E5E5E5] text-sm"
          data-testid="assessment-general-notes-input"
        />
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="assessment-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Assessment'}
        </Button>
      </div>
    </div>
  );
}
